import { useRecoilValue } from 'recoil';
import { Redirect } from 'react-router-dom';
import BootstrapForm from 'react-bootstrap/Form';

import * as K from 'common/constants';
import { HELP_ROUTE } from 'components/help/constants';
import ELEMENT from 'common/element';

import { searchParamState } from 'components/common/Managers/RouteStateManager.state';
import { accountDataState, logsAccessibleState } from 'components/account/account.state';

import OnBoarding from 'components/overview/LogOverviewPage/OnBoarding';
import Page from 'components/common/Page/Page';
import Button from 'components/common/Button/Button';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import Toolbar from 'components/common/Toolbar/Toolbar';
import TabbedPageHeader from 'components/common/TabbedPageHeader/TabbedPageHeader';
import Tabs from 'components/common/Tabs/Tabs';
import HelpIcon from '@mui/icons-material/Help';

import LogOverviewFilter from './LogOverviewFilter';
import LogOverviewSourcesContainer from './LogOverviewSourcesContainer';

import './LogOverviewPage.scss';

/**
 * This component is for the log overview page. It has two tabs, Sources & Services.
 * Sources shows a list of all the sources, ServerHosts and K8s-Clusters that are sending the logs.
 */
function LogOverviewPage(): JSX.Element {
  const currentTab = useRecoilValue(searchParamState(K.URL_PARAM.TAB));
  const accountData = useRecoilValue(accountDataState);

  // TODO: this would ideally be handled in the {@link Router} like our other permissions-based routing adjustments.
  //  However, since the logic is different, the implementation in Router might change, and there are special cases
  //  where the server returns this route, this is a one-off special case for now.
  const logsAccessible = useRecoilValue(logsAccessibleState);
  if (!logsAccessible) {
    return <Redirect to={K.ROUTE.ROOT} />;
  }

  const headerItems = [
    <>
      <div className="LogOverviewPage__FilterInputContainer">
        <BootstrapForm.Label className="LogOverviewPage__FilterInputLabel">Filter:</BootstrapForm.Label>
        <LogOverviewFilter />
      </div>
    </>,
    <Button
      href={HELP_ROUTE.HELP_GETTING_STARTED}
      size="sm"
      leftIcon={<HelpIcon fontSize="small" />}
      openInNewTab
      testId={ELEMENT.overviewHelp}
    >
      Help
    </Button>,
  ];

  // Redirect to the sources tab if there is no tab parameter
  if (!currentTab) {
    return <Redirect to={`${K.ROUTE.LOG_START}?tab=${K.SOURCES_TAB}`} />;
  }

  // Ensure that accountData has been fetched
  if (accountData?.dataAvailability === undefined) {
    return <LoadingPlaceholder />;
  }

  return (
    <Page docTitle="Log Sources" testId={ELEMENT.overviewPage}>
      {!accountData.dataAvailability ? (
        <OnBoarding />
      ) : (
        <div className="LogOverviewPage">
          <Tabs id="LogOverviewTabs" defaultActiveTab={currentTab || K.SOURCES_TAB}>
            <TabbedPageHeader
              className="LogOverviewPage__TabbedPageHeader"
              title="Overview"
              headerItems={[<Toolbar key="LogOverviewPage__Toolbar" items={headerItems} />]}
            />
            <div className="LogOverviewPage__PageContent">
              <LogOverviewSourcesContainer />
            </div>
          </Tabs>
        </div>
      )}
    </Page>
  );
}

export default LogOverviewPage;
